import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { LocationTypeIcon } from '@/features/booking/LocationField';
import { useLocale } from '@/i18n/LocaleContext';
import { localizedPath } from '@/i18n/locales';
import type { Trip } from '@/services/api/types';
import { formatDistance, formatDuration } from '@/shared/format';

/**
 * A clickable route card — origin, destination, duration and distance —
 * that jumps straight into that trip's booking flow. Shared by the landing page's
 * "frequent trips" and "featured trips" sections, which differ only in which
 * trips they show.
 *
 * <p>Shows a photo for each end of the route that has one — side by side,
 * origin then destination, when both do; just the one that has it when only
 * one does (every route here is sold in both directions, so the same
 * location is often the pickup on one card and the destination on another).
 * Omits the image row entirely only when neither end has one, the same way
 * {@code ServicesSection} degrades to a text-only card rather than a
 * placeholder box.
 */
export function TripShortcutCard({ trip }: { trip: Trip }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useLocale();

  const images: { url: string; alt: string }[] = [];
  if (trip.origin.imageUrl) images.push({ url: trip.origin.imageUrl, alt: trip.origin.name });
  if (trip.destination.imageUrl) images.push({ url: trip.destination.imageUrl, alt: trip.destination.name });

  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea
        onClick={() => navigate(localizedPath('trips', locale, trip.slug))}
        sx={{ height: '100%' }}
      >
        {images.length > 0 && (
          <Stack direction="row" sx={{ height: 120 }}>
            {images.map((image) => (
              <CardMedia
                key={image.url}
                component="img"
                image={image.url}
                alt={image.alt}
                loading="lazy"
                sx={{ flex: 1, minWidth: 0, height: '100%', objectFit: 'cover' }}
              />
            ))}
          </Stack>
        )}
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <LocationTypeIcon type={trip.origin.type} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {trip.origin.name}
              </Typography>
              <ArrowForwardIcon fontSize="small" color="action" />
              <LocationTypeIcon type={trip.destination.type} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {trip.destination.name}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {formatDuration(trip.durationMinutes, locale)} · {t('common.approxDuration')}
              {' · '}
              {formatDistance(trip.distanceKm, locale)}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default TripShortcutCard;
